import React, { useEffect, useRef, useState } from 'react';
import AmountCalItem from './AmountCalItem';
import { PlusCircleIcon } from '@heroicons/react/24/solid';
import { ClipboardDocumentListIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { validateNumberInput, numberFields } from '../../../../utils/formValidations';
import { formatAmount, formatAmountRound } from '../../../../utils/formatAmount';
import { generateBlacklistWarningMessage } from '../../../../utils/shopNameMapping';
import BlacklistWarningModal from '../../../../sharedComponents/BlacklistWarningModal';
import { TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { selectRateByShop, selectStandardRate } from '../../../../redux/features/todayRateSlice';
import useGetCustomerProfile from '../../../../hooks/useGetCustomerProfile';
import useManageOrder from '../../../order/useManageOrder';
import Typography from '@mui/material/Typography';
import GetWebsiteService from '../../../../services/GetWebsiteService';
import OrderByOptionService from '../../../setting/orderByOption/OrderByOptionService';
import useBankTransferOnly from '../../../../hooks/useBankTransferOnly';
import { COMPANY_BANK_NOTICE, companyBankText } from '../../../../utils/companyBank';

const womenList = ['Eve', 'Meen', 'Phat','Kluay','Pla','Nailah'];

const isPaymentLink = (url) => {
  try {
    const { hostname } = new URL(url);
    return hostname.includes('beamcheckout.com') || hostname.startsWith('pay.');
  } catch {
    return false;
  }
};

const AmountCal = ({defaultProfile, user, onClose}) => {
  const [inputList, setInputList] = useState([{ link: '', qty: 1, shipDetail: null }]); // Change default to null
  const [combinedText, setCombinedText] = useState('');
  const [rate, setRate] = useState({});
  const [isDown, setIsDown] = useState(false)
  const [isHandCarry, setIsHandCarry] = useState(false) // Add this state
  const [copyMessage, setCopyMessage] = useState('')
  const [linkRequested, setLinkRequested] = useState(false)
  const [isLoadingLink, setIsLoadingLink] = useState(false) // Add this state
  const [gender, setGender] = useState('ครับ')
  const [customerContact, setCustomerContact] = useState('');
  const [customerProfile, setCustomerProfile] = useState(null);
  // While links are off, company customers get the company account appended on copy
  const bankTransferOnly = useBankTransferOnly();
  const needsCompanyBank = bankTransferOnly && !!customerProfile?.isCompanyCustomer;
  const [paymentUrl, setPaymentUrl] = useState(null); // Store the payment URL
  const [currentIsCreditCard, setCurrentIsCreditCard] = useState(false); // Track current payment method
  const [baseText, setBaseText] = useState(''); // Store text without payment link
  const [blacklistWarningQueue, setBlacklistWarningQueue] = useState([]);
  // Bot buyers a Mercari item can be pinned to ("ส่งการ์ดให้คนนี้เท่านั้น", owner 2026-11-02): on, with a LINE channel.
  const [botBuyers, setBotBuyers] = useState([]);
  useEffect(() => {
    OrderByOptionService.getActiveOrderByOptions()
      .then((res) => setBotBuyers((res.data || []).filter((b) => Boolean(b && b.dispatchEnabled && (b.dispatchLineId || b.lineGroupId)))))
      .catch(() => setBotBuyers([]));
  }, []);
  const [currentBlacklistWarning, setCurrentBlacklistWarning] = useState(null);
  const [loadingRows, setLoadingRows] = useState(new Set());
  const [rowFetchStatus, setRowFetchStatus] = useState({});
  const [showBulkPaste, setShowBulkPaste] = useState(false);
  const [bulkPasteText, setBulkPasteText] = useState('');
  const [isBulkFetching, setIsBulkFetching] = useState(false);
  const fetchTimers = useRef({});
  const contactFetchTimer = useRef(null);

  const handleCloseBlacklistWarning = () => {
    setBlacklistWarningQueue(prev => {
      const remaining = prev.slice(1);
      if (remaining.length > 0) {
        setCurrentBlacklistWarning(remaining[0]);
      } else {
        setCurrentBlacklistWarning(null);
      }
      return remaining;
    });
  };

  const applyItemData = (item, data) => {
    const { finalPrice, deliveryFee } = data;
    const updated = { ...item };
    // Anshin appraisal availability (null = none, 0 = free, >0 = fee in JPY) — info badge only
    updated.appraisalFee = data.appraisalFee ?? null;
    // Original item title for categorization (carried into the pre-payment record → order)
    updated.itemTitle = data.title ?? null;
    if (finalPrice) updated.amount = finalPrice;
    if (deliveryFee === '0') {
      updated.deliveryFeeJp = '0';
      updated.shipDetail = 'free';
      updated.isCod = false;
    } else if (deliveryFee === 'COD') {
      updated.deliveryFeeJp = null;
      updated.shipDetail = 'cod';
      updated.isCod = true;
    } else if (deliveryFee && !isNaN(deliveryFee)) {
      updated.deliveryFeeJp = deliveryFee;
      updated.shipDetail = null;
      updated.isCod = false;
    }
    return updated;
  };

  const fetchItemDataForRow = async (index, url) => {
    setLoadingRows(prev => new Set(prev).add(index));
    try {
      const [itemRes, feesRes] = await Promise.allSettled([
        GetWebsiteService.getItemDataFromUrl(url),
        GetWebsiteService.getWebsiteFeesByUrl(url, customerProfile?.id),
      ]);

      const fees = feesRes.status === 'fulfilled' ? feesRes.value.data : {};
      const websiteAdditionalRate = parseFloat(fees.websiteAdditionalRate || 0);
      const websiteAdditionalTransactionFee = parseFloat(fees.websiteAdditionalTransactionFee || 0);
      const webName = fees.webName || '';

      const itemData = itemRes.status === 'fulfilled' && itemRes.value.data.success ? itemRes.value.data.data : null;
      const hasData = itemData && (itemData.finalPrice || itemData.deliveryFee);
      setRowFetchStatus(prev => ({ ...prev, [index]: hasData ? 'success' : 'unsupported' }));

      setInputList(prev => {
        let updated = prev.map((item, i) => {
          if (i !== index) return item;
          const base = { ...item, websiteAdditionalRate, websiteAdditionalTransactionFee, webName };
          return hasData ? applyItemData(base, itemData) : base;
        });
        const newText = generateCopyText(updated);
        setCombinedText(newText);
        setBaseText(newText);
        return updated;
      });
    } catch (e) {
      console.error('Error fetching item data for row', index, e);
      setRowFetchStatus(prev => ({ ...prev, [index]: 'error' }));
    } finally {
      setLoadingRows(prev => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
    }
  };

  const handleBulkPaste = async () => {
    const urlRegex = /https?:\/\/[^\s\n]+/g;
    const urls = [...new Set(bulkPasteText.match(urlRegex) || [])].filter(u => !isPaymentLink(u));
    if (urls.length === 0) {
      toast.warning('ไม่พบ URL ในข้อความ');
      return;
    }

    const newList = urls.map(url => ({ url, qty: 1, shipDetail: null, amount: '', deliveryFeeJp: undefined }));
    setInputList(newList);
    setRowFetchStatus({});
    setShowBulkPaste(false);
    setBulkPasteText('');
    clearLink();
    const initText = generateCopyText(newList);
    setCombinedText(initText);
    setBaseText(initText);

    setIsBulkFetching(true);
    setLoadingRows(new Set(urls.map((_, i) => i)));

    try {
      const [itemResults, feesResults] = await Promise.all([
        Promise.allSettled(urls.map(url => GetWebsiteService.getItemDataFromUrl(url))),
        Promise.allSettled(urls.map(url => GetWebsiteService.getWebsiteFeesByUrl(url, customerProfile?.id))),
      ]);

      const newStatuses = {};
      const finalList = newList.map((item, i) => {
        const r = itemResults[i];
        const f = feesResults[i];
        const fees = f.status === 'fulfilled' ? f.value.data : {};
        const withFees = {
          ...item,
          websiteAdditionalRate: parseFloat(fees.websiteAdditionalRate || 0),
          websiteAdditionalTransactionFee: parseFloat(fees.websiteAdditionalTransactionFee || 0),
          webName: fees.webName || '',
        };
        if (r.status === 'fulfilled' && r.value.data.success && r.value.data.data) {
          const { finalPrice, deliveryFee } = r.value.data.data;
          newStatuses[i] = (finalPrice || deliveryFee) ? 'success' : 'unsupported';
          return applyItemData(withFees, r.value.data.data);
        }
        newStatuses[i] = r.status === 'fulfilled' ? 'unsupported' : 'error';
        return withFees;
      });

      setInputList(finalList);
      setRowFetchStatus(newStatuses);
      setLoadingRows(new Set());
      const updatedText = generateCopyText(finalList);
      setCombinedText(updatedText);
      setBaseText(updatedText);
    } finally {
      setIsBulkFetching(false);
      setLoadingRows(new Set());
    }
  };

  const { getProfileFromContact } = useGetCustomerProfile();
  const { createPrePaymentLink } = useManageOrder();
  const shopName = useSelector((state) => state.shop.name);
  const todayRate = useSelector(state => selectRateByShop(state, shopName));
  const standardRate = useSelector(selectStandardRate);

  // Item fields the link carries: a change after the link was made means a new link (the pinned buyer travels with the item too).
  const PRICE_AFFECTING_KEYS = new Set(['url', 'amount', 'deliveryFeeJp', 'qty', 'dispatchPinnedBuyerId']);

  const handleInputChange = (e, key, index) => {
    let { value } = e.target;
    if (numberFields.has(key)) {
			value = validateNumberInput(value, inputList[index]);
		}
    const list = [...inputList];
    list[index][key] = value;
    setInputList(list);
    const newText = generateCopyText(list);

    if (PRICE_AFFECTING_KEYS.has(key) && linkRequested) {
      clearLink();
    }

    setCombinedText(newText);
    setBaseText(newText);

    if (key === 'url') {
      setRowFetchStatus(prev => { const next = { ...prev }; delete next[index]; return next; });
      clearTimeout(fetchTimers.current[index]);
      if (value.trim() && !isPaymentLink(value.trim())) {
        fetchTimers.current[index] = setTimeout(() => {
          fetchItemDataForRow(index, value.trim());
        }, 500);
      }
    }
  };

  // Add handler for shipping detail changes
  const handleShippingChange = (shipDetail, index) => {
    const list = [...inputList];
    let newFee = list[index].deliveryFeeJp; // Keep existing value by default
    let newShipDetail = shipDetail;
    
    // Toggle functionality - if clicking the same button, deselect (set to null)
    if (list[index].shipDetail === shipDetail) {
      newShipDetail = null;
      newFee = list[index].deliveryFeeJp; // Keep existing deliveryFeeJp value
    } else {
      // Update deliveryFee based on shipDetail selection
      if (shipDetail === 'free') {
        newFee = '0';
      } else if (shipDetail === 'cod') {
        newFee = null; // Set to null for COD
      } else if (shipDetail === 'unknown') {
        newFee = null; // Set to null for unknown
      }
    }
    
    const oldFeeNum = Number(list[index].deliveryFeeJp || 0);
    const newFeeNum = Number(newFee || 0);
    const oldIsCod = !!list[index].isCod;
    const newIsCod = (newShipDetail === 'cod');
    // Only invalidate the link if the actual delivery amount changes.
    // isCod switching only matters when there is a non-zero delivery fee (rate selection affects total).
    const deliveryAmountChanged = oldFeeNum !== newFeeNum || (oldIsCod !== newIsCod && oldFeeNum > 0);

    list[index].shipDetail = newShipDetail;
    list[index].deliveryFeeJp = newFee;
    list[index].isCod = newIsCod;

    setInputList(list);
    const newText = generateCopyText(list);
    if (deliveryAmountChanged) {
      clearLink();
      setCombinedText(newText);
      setBaseText(newText);
    } else if (linkRequested && paymentUrl) {
      setBaseText(newText);
      setCombinedText(newText + getPaymentLinkSuffix(paymentUrl, currentIsCreditCard));
    } else {
      setCombinedText(newText);
      setBaseText(newText);
    }
  };

  const handleAddInput = () => {
    setInputList([...inputList, { link: '' , qty: 1, shipDetail: null }]); // Change default to null
  };

  const handleRemoveInput = (index) => {
    if (inputList.length > 1) {
      const list = [...inputList];
      list.splice(index, 1);
      setInputList(list);
      setRowFetchStatus(prev => {
        const next = {};
        Object.entries(prev).forEach(([key, val]) => {
          const k = parseInt(key);
          if (k < index) next[k] = val;
          else if (k > index) next[k - 1] = val;
        });
        return next;
      });
      const newText = generateCopyText(list);
      clearLink();
      setCombinedText(newText);
      setBaseText(newText);
    }
  };

  const handleCopyText = () => {
    if (!customerContact.trim()) {
      toast.warning("กรุณาใส่ชื่อลูกค้าก่อน");
      return;
    }

    if(combinedText.includes('NaN') || combinedText.includes('undefined')){
      toast.warning("ข้อมูลไม่ครบถ้วน");
    } else {
      const textToCopy = needsCompanyBank
        ? `${combinedText}\n--------------------------\n${companyBankText(gender)}`
        : combinedText;
      navigator.clipboard.writeText(textToCopy)
        .then(() => setCopyMessage('Copied!'))
        .catch(() => setCopyMessage('******** Failed to copy ********'));

      setTimeout(() => setCopyMessage(''), 1500);
    }
  };

  const clearLink = () => {
    setLinkRequested(false);
    setPaymentUrl(null);
  };

  const handleRateChange = (name, value) => {
    if (numberFields.has(name)) {
			value = validateNumberInput(value, rate[name]);
		}
    clearLink();
    setRate(prevState => ({
      ...prevState,
      [name]: value
    }));
  }

  const handleContactChange = (e) => {
    const contact = e.target.value;
    setCustomerContact(contact);

    // Immediately clear everything when field is emptied
    if (!contact.trim()) {
      clearTimeout(contactFetchTimer.current);
      setLinkRequested(false);
      setPaymentUrl(null);
      setCurrentIsCreditCard(false);
      setCustomerProfile(null);
      setRate({
        productRate: shopName === "tama" ? todayRate : defaultProfile.productRate,
        downRate: defaultProfile.downRate,
        transactionFee: shopName === "tama" ? defaultProfile.transactionFee : 0
      });
      return;
    }

    // Debounce the profile lookup — nothing else changes while user is typing
    clearTimeout(contactFetchTimer.current);
    contactFetchTimer.current = setTimeout(async () => {
      try {
        const rawProfile = await getProfileFromContact(contact.trim());
        // Enforce case-sensitive match: reject if stored contact differs in case
        const profile = rawProfile && rawProfile.contact?.trim() === contact.trim() ? rawProfile : null;
        if (profile) {
          setCustomerProfile(profile);
          // A confirmed new profile invalidates any existing payment link
          setLinkRequested(false);
          setPaymentUrl(null);
          setCurrentIsCreditCard(false);
          // Update rates from profile
          if (profile.isFlowRate) {
            const percentageAdjustment = (todayRate / standardRate) - (profile?.extraPercentDiscount || 0);
            const newRate = standardRate * percentageAdjustment - (profile?.extraFlowDiscount || 0);
            const roundedRate = Math.round(newRate * 10000) / 10000;
            setRate(prevRate => ({
              ...prevRate,
              productRate: roundedRate,
              downRate: profile.downRate,
              transactionFee: shopName === "tama" ? profile.transactionFee : 0
            }));
          } else {
            setRate(prevRate => ({
              ...prevRate,
              productRate: profile.productRate,
              downRate: profile.downRate,
              transactionFee: shopName === "tama" ? profile.transactionFee : 0
            }));
          }
          // Check blacklist
          if (profile.isBlacklistedInOtherShops && profile.blacklistedShops?.length > 0) {
            const warningMessages = profile.blacklistedShops.map(blacklistedShop => {
              const formattedAmount = formatAmountRound(blacklistedShop.totalPendingAmount);
              return generateBlacklistWarningMessage(blacklistedShop.shop, formattedAmount, blacklistedShop.source);
            });
            setBlacklistWarningQueue(warningMessages);
            if (warningMessages.length > 0) {
              setCurrentBlacklistWarning(warningMessages[0]);
            }
          }
        } else {
          // Not found (or case mismatch) — clear profile and reset rates to default
          setCustomerProfile(null);
          setRate({
            productRate: shopName === "tama" ? todayRate : defaultProfile.productRate,
            downRate: defaultProfile.downRate,
            transactionFee: shopName === "tama" ? defaultProfile.transactionFee : 0
          });
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    }, 400);
  };

  const handleRequestLink = async () => {
    if (!customerContact.trim()) {
      toast.warning("กรุณาใส่ชื่อลูกค้าก่อน");
      return;
    }

    // Check if customer profile exists and isCompanyCustomer is true
    if (customerProfile && !customerProfile.isCompanyCustomer) {
      toast.warning("ไม่ต้องสร้างลิ้งค์");
      return;
    }

    // Validate that all items have required data
    const hasInvalidItems = inputList.some(item => 
      !item.url || !item.amount || item.amount === 'NaN' || item.amount === 'undefined'
    );

    if (hasInvalidItems) {
      toast.warning("ข้อมูลสินค้าไม่ครบถ้วน");
      return;
    }

    // Prevent double calls
    if (isLoadingLink || linkRequested) {
      return;
    }

    setIsLoadingLink(true); // Set loading state

    try {
      // Prepare items array
      const items = inputList.map(item => ({
        url: item.url,
        itemTitle: item.itemTitle ?? null,
        amount: Number(item.amount),
        deliveryFeeJp: item.deliveryFeeJp, // Send as is, can be null, undefined, or number
        qty: Number(item.qty) || 1,
        isCod: item.isCod || false, // Send isCod flag to backend
        // ส่งตรวจ requested → fee charged inside the pre-payment link (0 = free ส่งตรวจ)
        appraisalFee: item.includeInspection && item.appraisalFee != null ? Number(item.appraisalFee) : null,
        dispatchPinnedBuyerId: item.dispatchPinnedBuyerId || null // the bot buyer picked for this item, else the queue
      }));

      console.log(items)

      // Prepare request data (default to QR code, can be toggled after link creation)
      const requestData = {
        contact: customerContact,
        items: items,
        productRate: rate.productRate,
        transactionFee: rate.transactionFee || 0,
        shop: shopName,
        isHandCarry: isHandCarry,
        isCreditCard: false, // Default to QR code, toggle button will handle switching
        isDown: isDown,
        downRate: rate.downRate || 0
      };

      const response = await createPrePaymentLink(requestData);
      
      if (response && response.data && response.data.paymentUrl) {
        // Store base text (text before payment link)
        setBaseText(combinedText);
        setPaymentUrl(response.data.paymentUrl);
        setCurrentIsCreditCard(false); // Default to QR code initially
        
        // Append the payment link to the existing text (no credit card fee when QR)
        const paymentLinkText = getPaymentLinkSuffix(response.data.paymentUrl, false);
        
        const updatedText = combinedText + paymentLinkText;
        setCombinedText(updatedText);
        
        // Mark link as requested to show toggle button
        setLinkRequested(true);
        
        toast.success("สร้างลิ้งค์ชำระเงินเรียบร้อย");
      } else {
        console.log("GASDGGG", response)
        const errorMessage = response?.response?.data?.message || "เกิดข้อผิดพลาดในการสร้างลิ้งค์";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error creating payment link:', error);
      const errorMessage = error?.response?.data?.message || "เกิดข้อผิดพลาดในการสร้างลิ้งค์";
      toast.error(errorMessage);
    } finally {
      setIsLoadingLink(false); // Always reset loading state
    }
  };

  const handleTogglePaymentMethod = async () => {
    if (!customerContact.trim()) {
      toast.warning("กรุณาใส่ชื่อลูกค้าก่อน");
      return;
    }

    // Validate that all items have required data
    const hasInvalidItems = inputList.some(item => 
      !item.url || !item.amount || item.amount === 'NaN' || item.amount === 'undefined'
    );

    if (hasInvalidItems) {
      toast.warning("ข้อมูลสินค้าไม่ครบถ้วน");
      return;
    }

    // Prevent double calls
    if (isLoadingLink) {
      return;
    }

    setIsLoadingLink(true);

    try {
      // Toggle the credit card state
      const newIsCreditCard = !currentIsCreditCard;
      
      // Prepare items array
      const items = inputList.map(item => ({
        url: item.url,
        itemTitle: item.itemTitle ?? null,
        amount: Number(item.amount),
        deliveryFeeJp: item.deliveryFeeJp,
        qty: Number(item.qty) || 1,
        isCod: item.isCod || false,
        appraisalFee: item.includeInspection && item.appraisalFee != null ? Number(item.appraisalFee) : null,
        dispatchPinnedBuyerId: item.dispatchPinnedBuyerId || null
      }));

      // Prepare request data with toggled credit card state
      const requestData = {
        contact: customerContact,
        items: items,
        productRate: rate.productRate,
        transactionFee: rate.transactionFee || 0,
        shop: shopName,
        isHandCarry: isHandCarry,
        isCreditCard: newIsCreditCard,
        isDown: isDown,
        downRate: rate.downRate || 0
      };

      const response = await createPrePaymentLink(requestData);
      
      if (response && response.data && response.data.paymentUrl) {
        // Update stored values
        setPaymentUrl(response.data.paymentUrl);
        setCurrentIsCreditCard(newIsCreditCard);
        
        // Update payment link text (include credit card fee line when newIsCreditCard)
        const paymentLinkText = getPaymentLinkSuffix(response.data.paymentUrl, newIsCreditCard);
        
        // Update combined text with new payment link (replace the old one)
        const updatedText = baseText + paymentLinkText;
        setCombinedText(updatedText);
        
        toast.success(`เปลี่ยนเป็น ${newIsCreditCard ? 'Credit Card' : 'QR Code'} เรียบร้อย`);
      } else {
        const errorMessage = response?.response?.data?.message || "เกิดข้อผิดพลาดในการเปลี่ยนวิธีการชำระเงิน";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error toggling payment method:', error);
      const errorMessage = error?.response?.data?.message || "เกิดข้อผิดพลาดในการเปลี่ยนวิธีการชำระเงิน";
      toast.error(errorMessage);
    } finally {
      setIsLoadingLink(false);
    }
  };

  useEffect(() => {
    if(womenList.includes(user.nickname)){
      setGender('คะ')
    }
  },[user])

  // Grand total in THB (same logic as summary line) - for credit card fee calculation
  const getGrandTotalTHB = (currentInputList = inputList) => {
    let productTotal = 0;
    let websiteRateExtra = 0;
    let websiteTransactionFeeTotal = 0;
    currentInputList.forEach(obj => {
      const amountNum = Number(obj.amount) || 0;
      const deliveryFeeNum = Number(obj.deliveryFeeJp) || 0;
      const qty = Number(obj.qty) || 1;
      const itemYen = obj.deliveryFeeJp !== undefined && obj.deliveryFeeJp !== null && obj.deliveryFeeJp !== ''
        ? amountNum * qty + deliveryFeeNum
        : amountNum * qty;
      productTotal += itemYen;
      websiteRateExtra += itemYen * (Number(obj.websiteAdditionalRate) || 0);
      websiteTransactionFeeTotal += Number(obj.websiteAdditionalTransactionFee) || 0;
    });
    const orderQty = currentInputList.length;
    const baseRate = rate.productRate || 0;
    const baseFee = rate.transactionFee || 0;
    // ส่งตรวจ fees: flat THB at base rate — never halved by down payment, never × qty
    const inspectionThb = currentInputList.reduce((s, obj) =>
      s + (obj.includeInspection && Number(obj.appraisalFee) > 0 ? Number(obj.appraisalFee) * baseRate : 0), 0);
    if (shopName === 'tama') {
      if (isDown) {
        return (productTotal * baseRate + websiteRateExtra) * (0.5 + (rate.downRate || 0) / 100) + orderQty * baseFee + websiteTransactionFeeTotal + inspectionThb;
      }
      return productTotal * baseRate + websiteRateExtra + orderQty * baseFee + websiteTransactionFeeTotal + inspectionThb;
    }
    if (isDown) {
      return (productTotal * baseRate + websiteRateExtra) * (0.5 + (rate.downRate || 0) / 100) + inspectionThb;
    }
    return productTotal * baseRate + websiteRateExtra + inspectionThb;
  };

  // Payment link suffix: with credit card fee line when isCreditCard (same as OrderAmount)
  const getPaymentLinkSuffix = (url, isCreditCard) => {
    if (isCreditCard) {
      const grandTotalTHB = getGrandTotalTHB();
      const originalAmount = typeof grandTotalTHB === 'number' ? grandTotalTHB : parseFloat(String(grandTotalTHB).replace(/[^\d.]/g, '')) || 0;
      const totalWithFee = originalAmount + originalAmount * 0.035;
      return `\n--------------------------\n ยอดรวม + ค่าธรรมเนียม 3.5% = ${totalWithFee.toFixed(2)} บาท\n🔗 ลิงก์สำหรับชำระเงิน :\n${url}`;
    }
    return `\n--------------------------\n🔗 ลิงก์สำหรับชำระเงิน :\n${url}`;
  };

  // Update generateCopyText to accept inputList as parameter
  const generateCopyText = (currentInputList = inputList) => {
    let totalAmount = 0;
    const lines = currentInputList.map(obj => {
      const { url, amount, deliveryFeeJp, qty } = obj;
  
      // Ensure amount and deliveryFeeJp are treated as numbers
      const amountNum = Number(amount);
      const deliveryFeeNum = Number(deliveryFeeJp) || 0; // Handle null/undefined as 0
  
      let line = `${url}\n${amountNum*qty}`;
      if (deliveryFeeJp !== undefined && deliveryFeeJp !== null && deliveryFeeJp !== '') {
        line += ` + ${deliveryFeeNum}`;
        totalAmount += amountNum*qty + deliveryFeeNum ;
      } else {
        totalAmount += amountNum*qty;
      }
      if (obj.includeInspection && obj.appraisalFee != null) {
        line += Number(obj.appraisalFee) > 0 ? ` + ส่งตรวจ ${Number(obj.appraisalFee)}` : ' (ส่งตรวจฟรี)';
      }

      return line;
    });
    const orderQty = currentInputList.length;

    // ส่งตรวจ (inspection) fees — flat per item when ticked, never multiplied by qty
    const inspectYen = currentInputList.reduce((s, obj) =>
      s + (obj.includeInspection && Number(obj.appraisalFee) > 0 ? Number(obj.appraisalFee) : 0), 0);

    // Compute per-item website fees
    const websiteTransactionFeeTotal = currentInputList.reduce((s, obj) => s + (Number(obj.websiteAdditionalTransactionFee) || 0), 0);
    const websiteRateExtra = currentInputList.reduce((s, obj) => {
      const amountNum = Number(obj.amount) || 0;
      const deliveryFeeNum = Number(obj.deliveryFeeJp) || 0;
      const qty = Number(obj.qty) || 1;
      const itemYen = (obj.deliveryFeeJp !== undefined && obj.deliveryFeeJp !== null && obj.deliveryFeeJp !== '')
        ? amountNum * qty + deliveryFeeNum : amountNum * qty;
      return s + itemYen * (Number(obj.websiteAdditionalRate) || 0);
    }, 0);

    // Group website fees by name: { webName: { count, total } }
    const websiteFeeByName = {};
    currentInputList.forEach(obj => {
      const fee = Number(obj.websiteAdditionalTransactionFee) || 0;
      if (fee > 0 && obj.webName) {
        if (!websiteFeeByName[obj.webName]) websiteFeeByName[obj.webName] = { count: 0, total: 0 };
        websiteFeeByName[obj.webName].count++;
        websiteFeeByName[obj.webName].total += fee;
      }
    });
    const websiteFeeEntries = Object.entries(websiteFeeByName);

    // Build inline website fee line(s)
    const websiteFeeLines = websiteFeeEntries.length > 0
      ? '\n' + websiteFeeEntries.map(([webName, { count, total: wTotal }]) =>
          `ค่าบริการพิเศษสำหรับรายการ ${webName} ${count} รายการ เป็นยอด ${wTotal} บาท`
        ).join('\n')
      : '';

    const inspectThb = inspectYen * (rate.productRate || 0);
    const inspectLine = inspectYen > 0
      ? `\nค่าบริการส่งตรวจ ${inspectYen} เยน * ${rate.productRate} = ${formatAmount(inspectThb)} บาท`
      : '';

    let summaryLine = '';
    if (shopName === 'tama') {
      const baseTransactionFeeTotal = orderQty * (rate.transactionFee || 0);
      const hasBothFees = baseTransactionFeeTotal > 0 && websiteTransactionFeeTotal > 0;
      if (isDown) {
        const productThb = totalAmount * (rate.productRate || 0) + websiteRateExtra;
        const grandTotal = productThb * (0.5 + (rate.downRate || 0) / 100) + baseTransactionFeeTotal + websiteTransactionFeeTotal + inspectThb;
        let feeSection = '';
        if (hasBothFees) {
          feeSection = `ค่าบริการสั่งซื้อ\n` +
            `- ค่าบริการ ${orderQty} รายการ เป็นยอดทั้งหมด ${formatAmount(baseTransactionFeeTotal)} บาท` +
            websiteFeeLines.replace(/\n/g, '\n- ');
        } else if (baseTransactionFeeTotal > 0) {
          feeSection = `รวมค่าค่าบริการ ${orderQty} รายการ เป็นยอดทั้งหมด ${formatAmount(baseTransactionFeeTotal)} บาท`;
        } else {
          feeSection = websiteFeeLines.trim();
        }
        summaryLine = `\n-----------------\n` +
          `ยอดรวมที่ต้องการจะมัดจำ ${totalAmount} เยน * ${rate.productRate} = ${formatAmount(productThb)} บาท \n\n` +
          `ยอดชำระมัดจำ ${formatAmount(productThb)} / 2 = ${formatAmount(productThb / 2)} บาท\n` +
          `ค่าบริการมัดจำ ${formatAmount(productThb)} * ${(rate.downRate || 0) / 100} = ${formatAmount(productThb * (rate.downRate || 0) / 100)} บาท\n\n` +
          feeSection + inspectLine +
          `\n\nรวมยอดชำระรอบแรก ${formatAmount(grandTotal)} บาท`;
      } else {
        const productThb = totalAmount * (rate.productRate || 0) + websiteRateExtra;
        const grandTotal = productThb + baseTransactionFeeTotal + websiteTransactionFeeTotal + inspectThb;
        let feeSection = '';
        if (hasBothFees) {
          feeSection = `ค่าบริการสั่งซื้อ\n` +
            `- ค่าบริการ ${orderQty} รายการ เป็นยอดทั้งหมด ${formatAmount(baseTransactionFeeTotal)} บาท` +
            websiteFeeLines.replace(/\n/g, '\n- ');
        } else if (baseTransactionFeeTotal > 0) {
          feeSection = `รวมค่าค่าบริการ ${orderQty} รายการ เป็นยอดทั้งหมด ${formatAmount(baseTransactionFeeTotal)} บาท`;
        } else {
          feeSection = websiteFeeLines.trim();
        }
        summaryLine = `\n-----------------\n` +
          `รวมค่าสินค้าเป็นยอด ${totalAmount} เยน * ${rate.productRate} = ${formatAmount(productThb)} บาท\n\n` +
          feeSection + inspectLine +
          `\n\nรวมทั้งหมดเป็นยอด ${formatAmount(grandTotal)} บาท`;
      }
    } else {
      const productThb = totalAmount * (rate.productRate || 0) + websiteRateExtra;
      if (isDown) {
        summaryLine = `\n-----------------\n` +
          `ยอดรวมที่ต้องการจะมัดจำ ${totalAmount} เยน * ${rate.productRate} = ${formatAmount(productThb)} บาท \n\n` +
          `ยอดชำระมัดจำ ${formatAmount(productThb)} / 2 = ${formatAmount(productThb / 2)} บาท\n` +
          `ค่าบริการมัดจำ ${formatAmount(productThb)} * ${(rate.downRate || 0) / 100} = ${formatAmount(productThb * (rate.downRate || 0) / 100)} บาท\n` +
          inspectLine +
          `\nรวมยอดชำระรอบแรก = ${formatAmount(productThb * (0.5 + (rate.downRate || 0) / 100) + inspectThb)} บาท`;
      } else {
        summaryLine = `\n-----------------\nรวมทั้งหมดเป็นยอด ${totalAmount} เยน * ${rate.productRate} = ${formatAmount(productThb)} บาท`;
        if (websiteTransactionFeeTotal > 0) {
          summaryLine += websiteFeeLines;
        }
        summaryLine += inspectLine;
        if (websiteTransactionFeeTotal > 0 || inspectYen > 0) {
          summaryLine += `\n\nรวมทั้งหมดเป็นยอด ${formatAmount(productThb + websiteTransactionFeeTotal + inspectThb)} บาท`;
        }
      }
    }

    const startLine = `ขออนุญาตสรุปยอดให้นะ${gender}\n\n`;
    return startLine + lines.join('\n') + summaryLine;
  }
  
  useEffect(() => {
    const newText = generateCopyText();
    clearLink();
    setCombinedText(newText);
    setBaseText(newText);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rate, isDown])

  useEffect(() => {
    setRate({
      productRate: shopName === "tama" ? todayRate : defaultProfile.productRate,
      downRate: defaultProfile.downRate,
      transactionFee: shopName === "tama" ? defaultProfile.transactionFee : 0
    })
  },[defaultProfile, todayRate, shopName])

  // When the customer is identified (or changes), re-check website fee exclusions
  // for items that already had fees loaded — without overwriting item prices.
  useEffect(() => {
    if (!customerProfile?.id) return;
    const itemsToRefetch = inputList
      .map((item, i) => ({ index: i, url: item.url, webName: item.webName }))
      .filter(item => item.url && item.webName);
    if (itemsToRefetch.length === 0) return;

    Promise.allSettled(
      itemsToRefetch.map(({ index, url }) =>
        GetWebsiteService.getWebsiteFeesByUrl(url, customerProfile.id)
          .then(res => ({ index, fees: res.data }))
      )
    ).then(results => {
      setInputList(prev => {
        const updated = [...prev];
        results.forEach(r => {
          if (r.status !== 'fulfilled') return;
          const { index, fees } = r.value;
          updated[index] = {
            ...updated[index],
            websiteAdditionalRate: parseFloat(fees.websiteAdditionalRate || 0),
            websiteAdditionalTransactionFee: parseFloat(fees.websiteAdditionalTransactionFee || 0),
            webName: fees.webName || '',
          };
        });
        const newText = generateCopyText(updated);
        setBaseText(newText);
        setCombinedText(newText);
        return updated;
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerProfile?.id])

  const hasEmptyAmount = inputList.some(item => !item.amount && item.amount !== 0);

  return (
    <div style={{ display: 'flex', position: 'relative' }}>
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-2 right-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-1 px-3 rounded z-10"
        >
          ✕ Close
        </button>
      )}
      <div>
        <div className='mb-3 flex items-center gap-2'>
          <TextField 
            label="Facebook/Line (ชื่อลูกค้า)" 
            onChange={handleContactChange}
            value={customerContact}
            sx={{width: 300}}
            placeholder="Enter customer contact"
          />
          {customerProfile?.smileName && (
            <TextField
              label="Next Order ID"
              value={`${customerProfile.smileName}${String((customerProfile.smileOrderNo ?? 0) + 1)}`}
              disabled
              sx={{ width: 180 }}
            />
          )}
        </div>

        <div className="relative">
          {inputList.map((item, idx) => (
            <AmountCalItem
              key={idx}
              data={item}
              handleInputChange={handleInputChange}
              handleShippingChange={handleShippingChange}
              handleRemoveInput={handleRemoveInput}
              index={idx}
              canRemove={inputList.length > 1}
              isLoading={loadingRows.has(idx)}
              fetchStatus={rowFetchStatus[idx]}
              botBuyers={botBuyers}
            />
          ))}
          <div className='flex items-center gap-3 mt-3'>
            <PlusCircleIcon onClick={handleAddInput} className="h-10 w-10 text-indigo-400 hover:text-indigo-600 cursor-pointer"/>
            <button
              onClick={() => setShowBulkPaste(true)}
              disabled={!customerProfile}
              className={`flex items-center gap-1 text-sm border px-3 py-1.5 rounded-lg transition-colors ${
                customerProfile
                  ? 'text-purple-600 hover:text-purple-800 border-purple-300 hover:border-purple-500'
                  : 'text-gray-400 border-gray-200 cursor-not-allowed'
              }`}
              title={customerProfile ? 'วาง URL หลายรายการพร้อมกัน' : 'ต้องพบข้อมูลลูกค้าก่อนใช้งาน'}
            >
              <ClipboardDocumentListIcon className="h-4 w-4" />
              Paste Bulk
              {!customerProfile && customerContact.trim() && (
                <span className="ml-1 text-xs text-orange-400">(ไม่พบลูกค้า)</span>
              )}
            </button>
          </div>
        </div>
      </div>
      
      <div className='ml-5'>
        {customerContact.trim() ? (
          <>
            <div className='flex flex-col gap-3 mb-3'>
              {/* Rate Settings Row */}
              <div className='flex items-center gap-3'>
                <TextField 
                  label="เรทสินค้า" 
                  onChange={(e) => handleRateChange('productRate',e.target.value)}
                  value={rate?.productRate}
                  sx={{width: 150}}
                />
                { shopName === 'tama' && 
                  <TextField 
                    label="ค่าบริการ" 
                    onChange={(e) => handleRateChange('transactionFee',e.target.value)}
                    value={rate?.transactionFee}
                    sx={{width: 150}}
                  />
                }
                {isDown && (
                  <TextField 
                    label="เรทมัดจำ (%)" 
                    onChange={(e) => handleRateChange('downRate',e.target.value)}
                    value={rate?.downRate}
                    sx={{width: 150}}
                    type="number"
                    inputProps={{ min: 0, max: 100 }}
                  />
                )}
              </div>

              {/* Payment Options Row */}
              <div className='flex items-center gap-4 p-3 bg-gray-50 rounded-lg'>
                <Typography variant="subtitle2" sx={{ minWidth: '80px', fontWeight: 'bold' }}>
                  ตัวเลือก:
                </Typography>
                <FormControlLabel 
                  control={
                    <Checkbox 
                      checked={isDown}
                      onChange={(e) => setIsDown(e.target.checked)} />
                    } 
                  label="มัดจำ" 
                />
                {customerProfile && customerProfile.isCompanyCustomer && (
                  <>
                    <FormControlLabel 
                      control={
                        <Checkbox 
                          checked={isHandCarry}
                          onChange={(e) => setIsHandCarry(e.target.checked)} />
                        } 
                      label="เป็นรายการหิ้ว" 
                    />
                  </>
                )}
              </div>

              {needsCompanyBank && (
                <div className='p-2 rounded bg-red-100 text-red-700 font-bold'>{COMPANY_BANK_NOTICE}</div>
              )}

              {/* Action Buttons Row */}
              <div className='flex items-center justify-between pt-2'>
                <div className='flex items-center gap-2'>
                  <button
                    className={`btn-primary ${hasEmptyAmount ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={handleCopyText}
                    disabled={hasEmptyAmount}
                    title={hasEmptyAmount ? 'มีรายการที่ยังไม่ได้ใส่ราคา' : ''}
                  >
                    Copy Text
                  </button>
                  {customerContact.trim() && !customerProfile ? (
                    <button 
                      className='bg-orange-400 text-white cursor-not-allowed px-4 py-2 rounded'
                      disabled={true}
                    >
                      ไม่พบข้อมูลลูกค้า
                    </button>
                  ) : customerProfile && customerProfile.isCompanyCustomer === false ? (
                    <button 
                      className='bg-gray-400 text-gray-600 cursor-not-allowed px-4 py-2 rounded'
                      disabled={true}
                    >
                      ไม่ต้องสร้างลิ้งค์
                    </button>
                  ) : needsCompanyBank ? (
                    <button
                      className='bg-gray-400 text-gray-600 cursor-not-allowed px-4 py-2 rounded'
                      disabled={true}
                    >
                      ปิดลิ้งค์ — โอนบัญชีบริษัท
                    </button>
                  ) : linkRequested ? (
                    <button 
                      className='bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
                      onClick={handleTogglePaymentMethod}
                      disabled={isLoadingLink || hasEmptyAmount}
                      title={hasEmptyAmount ? 'มีรายการที่ยังไม่ได้ใส่ราคา' : ''}
                    >
                      {isLoadingLink ? 'กำลังเปลี่ยน...' : `เปลี่ยนเป็น ${currentIsCreditCard ? 'QR Code' : 'Credit Card'}`}
                    </button>
                  ) : (
                    <button 
                      className={`px-4 py-2 rounded ${
                        isLoadingLink || hasEmptyAmount
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-green-500 hover:bg-green-600 text-white'
                      }`}
                      onClick={handleRequestLink}
                      disabled={isLoadingLink || hasEmptyAmount}
                      title={hasEmptyAmount ? 'มีรายการที่ยังไม่ได้ใส่ราคา' : ''}
                    >
                      {isLoadingLink ? 'กำลังสร้างลิ้งค์...' : 'ขอลิ้งค์'}
                    </button>
                  )}
                </div>
                
                {/* Status Indicators */}
                <div className='flex items-center gap-2'>
                  {isLoadingLink && (
                    <span className='bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-sm'>
                      กำลังประมวลผล...
                    </span>
                  )}
                  {isDown && (
                    <span className='bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm'>
                      มัดจำ
                    </span>
                  )}
                  {isHandCarry && (
                    <span className='bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-sm'>
                      หิ้ว
                    </span>
                  )}
                </div>
              </div>
            </div>
            {hasEmptyAmount && (
              <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-amber-50 border border-amber-300 rounded-lg text-amber-700 text-sm">
                <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                <span>มีรายการที่ยังไม่ได้ใส่ราคา — กรุณาตรวจสอบก่อน Copy หรือขอลิ้งค์</span>
              </div>
            )}
            <textarea
              value={combinedText}
              readOnly
              style={{ height: '600px', width: '600px' }}
              className='bg-gray-100'
            />
            <div className='text-xl'>{copyMessage}</div>
          </>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-xl text-gray-500 font-medium">
              กรุณาใส่ชื่อลูกค้าก่อน
            </div>
          </div>
        )}
      </div>

      {/* Blacklist Warning Modal */}
      <BlacklistWarningModal
        isOpen={currentBlacklistWarning !== null}
        onClose={handleCloseBlacklistWarning}
        message={currentBlacklistWarning}
      />

      {/* Bulk Paste Modal */}
      <Dialog open={showBulkPaste} onClose={() => setShowBulkPaste(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <div className="flex items-center gap-2">
            <ClipboardDocumentListIcon className="h-5 w-5 text-purple-600" />
            <span>วาง URL หลายรายการ</span>
          </div>
        </DialogTitle>
        <DialogContent>
          <p className="text-sm text-gray-500 mb-3">
            วางข้อความที่มี URL (Mercari / Yahoo / Mercari Shop) ระบบจะดึงราคาและค่าส่งให้อัตโนมัติ
          </p>
          <textarea
            className="w-full border border-gray-300 rounded-lg p-3 text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-purple-400"
            rows={10}
            placeholder={`ขออนุญาตสรุปยอดให้นะครับ\n\nhttps://jp.mercari.com/item/m60390754420\n20000 + 30\nhttps://jp.mercari.com/item/m20842511582\n24000 + 300`}
            value={bulkPasteText}
            onChange={e => setBulkPasteText(e.target.value)}
          />
          {bulkPasteText && (() => {
            const allUrls = [...new Set(bulkPasteText.match(/https?:\/\/[^\s\n]+/g) || [])];
            const payUrls = allUrls.filter(isPaymentLink);
            const productUrls = allUrls.filter(u => !isPaymentLink(u));
            return productUrls.length > 0 ? (
              <p className="text-xs text-purple-600 mt-1">
                พบ {productUrls.length} URL
                {payUrls.length > 0 && <span className="text-amber-500 ml-2">(ตัด {payUrls.length} payment link ออก)</span>}
              </p>
            ) : allUrls.length > 0 ? (
              <p className="text-xs text-red-500 mt-1">พบแต่ payment link — ไม่มี URL สินค้า</p>
            ) : (
              <p className="text-xs text-red-500 mt-1">ไม่พบ URL ในข้อความ</p>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setShowBulkPaste(false); setBulkPasteText(''); }} color="inherit">
            ยกเลิก
          </Button>
          <Button
            onClick={handleBulkPaste}
            variant="contained"
            disabled={isBulkFetching || !bulkPasteText.trim()}
            sx={{ backgroundColor: '#7c3aed', '&:hover': { backgroundColor: '#6d28d9' } }}
          >
            {isBulkFetching ? 'กำลังดึงข้อมูล...' : 'ดึงข้อมูล'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AmountCal;
